import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground shadow-primary-sm hover:shadow-primary-md transition-shadow",
                destructive:
                    "bg-destructive text-destructive-foreground hover-mouse:bg-destructive/90",
                outline:
                    "border border-input bg-background",
                secondary:
                    "bg-secondary text-secondary-foreground hover-mouse:bg-secondary/80",
                ghost: "",
                link: "text-primary underline-offset-4 hover-mouse:underline",
                "icon-box": "bg-background control-responsive border-[1px] border-solid border-border w-10 h-10 hover-mouse:bg-muted hover-mouse:border-accent text-primary",
                // ── 아이콘 전용 버튼 4종 ──
                // 1. 사이드바: 테두리 없음, 24×24, 아이콘 16px
                "icon-ghost": "size-6 p-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors [&_svg]:size-4",
                // 2. 헤더/우측메뉴: 32×32, 아이콘 20px
                "icon-panel": "size-8 p-1.5 rounded-control text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors [&_svg]:size-5 data-[state=on]:bg-muted",
                // 3. 채팅 입력창: 테두리만, 32×32, 아이콘 18px
                "icon-outline": "size-8 p-0 rounded-lg border border-border bg-transparent text-[color:rgb(var(--text-body))] opacity-80 hover:bg-accent overflow-hidden transition-colors [&_svg]:size-[18px]",
                // 4. 메시지 리액션: 작은 테두리, 28×28, 아이콘 16px
                "icon-reaction": "size-7 p-0 rounded-md border border-border bg-background text-muted-foreground hover:text-primary hover:bg-primary/10 hover:border-primary/20 shadow-sm transition-colors [&_svg]:size-4 data-[state=on]:text-primary data-[state=on]:bg-primary/10 data-[state=on]:border-primary/20",
                // 5. 파일관리 모바일 액션: h-32 px-8 rounded-8, 아이콘 16px
                "icon-filter": "h-8 px-2 rounded-control border border-border bg-transparent text-muted-foreground hover:bg-muted transition-colors [&_svg]:size-4",
                // 6. 파일관리 모바일 액션(강조): primary 틴트 배경
                "icon-filter-active": "h-8 px-2 rounded-control border border-primary/20 bg-primary/10 text-muted-foreground hover:bg-primary/15 transition-colors [&_svg]:size-4",
                // 7. PC 파일관리 라벨 버튼: Figma filter-label, hover 오렌지
                "filter-label": "h-8 px-2 gap-1.5 rounded-control border border-border bg-background text-label !font-normal hover:bg-primary/20 hover:text-primary hover:border-primary/20 [&_svg]:size-4",
            },
            size: {
                default: "h-8 px-2 py-2",
                sm: "h-9 px-2",
                lg: "h-11 px-8",
                icon: "h-9 w-9",
                none: "", // Add none size so we can override defaults for specific variants like icon-panel
            },
            radius: {
                default: "rounded-control",
                control: "rounded-control",
                card: "rounded-card",
                panel: "rounded-panel",
                dropdown: "rounded-dropdown",
                none: "rounded-none",
                self: "", // variant 자체에서 rounded를 정의하는 경우 기본 radius를 적용하지 않음
                // 반응형 variants
                "control-responsive": "rounded-control-mobile lg:rounded-control",
                "card-responsive": "rounded-card-mobile lg:rounded-card",
                "panel-responsive": "rounded-panel-mobile lg:rounded-panel",
            },
            iconSize: {
                xs: "[&_svg]:h-3 [&_svg]:w-3",      // 12px
                sm: "[&_svg]:h-4 [&_svg]:w-4",      // 16px
                md: "[&_svg]:h-5 [&_svg]:w-5",      // 20px
                lg: "[&_svg]:h-6 [&_svg]:w-6",      // 24px
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
            radius: "default",
            iconSize: "sm",  // 기본 16px
        },
        compoundVariants: [
            // icon-box variant default icon size (size omitted defaults to icon)
            {
                variant: "icon-box",
                size: "icon",
                className: "[&_svg]:h-5 [&_svg]:w-5",
            },
            // size variations
            {
                variant: "icon-box",
                size: "sm",
                className: "[&_svg]:h-3.5 [_svg]:w-3.5",
            },
            {
                variant: "icon-box",
                size: "lg",
                className: "[&_svg]:h-5 [_svg]:w-5",
            },
        ],
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size, radius, iconSize, asChild = false, ...props }, ref) => {
        // icon-box variant 에서는 size 를 지정하지 않으면 기본적으로 "icon" 으로 처리
        // icon- variant 및 filter-label은 자체 크기/반경을 정의하므로 기본 size/radius 무효화
        const isSelfContainedVariant = typeof variant === 'string' && (variant.startsWith('icon-') && variant !== 'icon-box' || variant === 'filter-label');
        const finalSize = variant === "icon-box" && !size ? "icon" : (isSelfContainedVariant && !size ? "none" : size);
        // 자체 rounded를 정의하므로 기본 radius(rounded-control)가 덮어쓰지 않도록 "self" 처리
        const finalRadius = isSelfContainedVariant && !radius ? "self" as const : radius;
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ variant, size: finalSize, radius: finalRadius, iconSize, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button"

export { Button, buttonVariants }
